import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import UIRadioGroup, { UIRadio } from "components/custom-ui/UIRadioGroup";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { PrimaryButton, SecondaryButton, UITextInput } from "components/ui";
import Classes, { CustomItemTypeCode, PositivePayCheckFile, PositivePayCheckFileRow, PositivePayCheckTemplate } from "model";
import React from "react";
import { SVGAsset } from "res";
import TASPositivePayCreateCheckTemplateForm from "./classes/TASPositivePayCreateCheckTemplateForm";
import TASPositivePayCreateTemplateFileExcludeWidget from "./components/TASPositivePayCreateTemplateFileExcludeWidget";
import TASPositivePayCreateTemplateFileFormatWidget from "./components/TASPositivePayCreateTemplateFileFormatWidget";
import TASPositivePayCreateTemplateFileMapColumnsWidget from "./components/TASPositivePayCreateTemplateFileMapColumnsWidget";
import TASPositivePayCreateTemplateFileMappingPreviewWidget from "./components/TASPositivePayCreateTemplateFileMappingPreviewWidget";
import TASPositivePayCreateTemplateFilePreviewWidget from "./components/TASPositivePayCreateTemplateFilePreviewWidget";
import TASPositivePayCreateTemplateItemTypeMappingWidget from "./components/TASPositivePayCreateTemplateItemTypeMappingWidget";
import "./styles.css";
import "./mobile.css";
import TASPositivePayCreateCheckTemplateFormUI from "./classes/TASPositivePayCreateCheckTemplateFormUI";
import CustomItemTypeCodeUI from "./classes/CustomItemTypeCodeUI";

/**
 * @function TASPositivePayCreateTemplatePage
 * @return {React.Component}
 * @exported true
 * @description The main component for the TASPositivePayCreateTemplatePage.
 */
export default function TASPositivePayCreateTemplatePage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);

    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(data.checkTemplate), ...props.ui ?? {} };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,
        onChangeCheckTemp: prop => value => {
            const crud = BCMSStateManagerCRUD.init(state);

            if(prop === "positivePayCheckFile") {
                value = PositivePayCheckFile.initByID("0")
                crud.update(["ui", "checkTemplate", "showFilePreview", "isActive"], true)
            }

            if(prop === "useCustomTransactionCode" && (value === false || value === "")) {
                const selectedColumns = state.data.selectedColumns.reduce((acc, item) => {
                    if(item.value.value === "Item Type Code") {
                        acc.push(
                            { id: item.id, value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } }
                        );
                    } else {
                        acc.push(item)
                    }
                    return acc;
                }, []);

                crud.update(["data", "selectedColumns"], selectedColumns)
            }
            
            crud.update(["data", "checkTemplate", prop], value)
            crud.submit(setState);
        },
        onChangeItemType: (id, prop) => val => {
            const checkTemplate = state.data.checkTemplate;
            let customItemTypeCodes = checkTemplate.customItemTypeCodes;
            const checkTemplateUI = state.ui.checkTemplate;
            let customItemTypeCodesUI = checkTemplateUI.customItemTypeCodes;

            customItemTypeCodes = customItemTypeCodes.reduce((acc, item) => {
                if(item.id === id) {
                    const modifiedItem = CustomItemTypeCode.init({ ...item.record, id: id });
                    modifiedItem[prop] = val;
                    acc.push(modifiedItem);
                } else {
                    acc.push(item)
                }
                return acc;
            }, []);

            const filterNewItem = customItemTypeCodes.filter(item => item.id === id)[0];

            customItemTypeCodesUI = customItemTypeCodesUI.reduce((acc, item) => {
                if(item.id === id) {
                    const modifiedItem = CustomItemTypeCode.init({ ...filterNewItem.record });
                    acc.push(
                        CustomItemTypeCodeUI.init({ ...item.record, customItemTypeCode: modifiedItem })
                    );
                } else {
                    acc.push(item)
                }
                return acc;
            }, []);

            BCMSStateManagerCRUD.init(state)
            .update(["data", "checkTemplate", "customItemTypeCodes"], customItemTypeCodes)
            .update(["ui", "checkTemplate", "customItemTypeCodes"], customItemTypeCodesUI)
            .submit(setState);
        },
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "template", prop], value)
                .submit(setState);
        },
        onDataChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", prop], value)
                .submit(setState);
        },
        onColumnChange: (id) => value => {
            // Map "mapColumn1" to id "0" since that is the indexing used in the "selectedColumns" array.
            var selectedMapColumnID = String(parseInt(id) + 1);
            var selectedMapColumnValue = value.value;
            let hasItemTypeCodeSelected = value.value === "Item Type Code";
            var selectedMapColumnKey = `mapColumn${ selectedMapColumnID }`;
            var alreadySelected = false;
            const crud =BCMSStateManagerCRUD.init(state);

            const selectedColumns = state.data.selectedColumns.reduce((acc, item) => {
                if (item.id === id) {
                    item.value = value;
                    item.value.isSelected = true;
                    
                }
                return [...acc, item]
            }, []);

            const mapColumns = state.data.mapColumns.reduce((acc, item) => {
                const selected = selectedColumns.find(column => column.value.id === item.id);

                if (item.isSelected !== undefined) {
                    item.isSelected = selected !== undefined ? true : false;
                }

                return [...acc, item]
            }, []);

            for(let i = 0; i < selectedColumns.length; i++) {
                const column = selectedColumns[i];
                const columnValue = column.value.value;

                if(columnValue === "Item Type Code") {
                    hasItemTypeCodeSelected = true
                }
            }

            crud.update(["data", "selectedColumns"], selectedColumns)
            crud.update(["data", "mapColumns"], mapColumns)
            crud.update(["data", "checkTemplate", selectedMapColumnKey ], selectedMapColumnValue)
            crud.update(["data", "checkTemplate", "useCustomTransactionCode" ], hasItemTypeCodeSelected)
            crud.submit(setState);
        },
        // Function adds a new template. "navigateCallback" is a function used to prevent the "useRoute" hook
        // from being initialized in the parent component due to a rerender that happens when opening the main menu.
        onAdd: (navigateCallback) => evt => {
            const defaultCheckTemplate = BCMSDefaultModel.positivePayCheckTemplate();
            const checkTemplateRecord = state.data.checkTemplate.record;
            const itemTypeCodeMap = {
                "Payee": "payee",
                "Amount": "amount",
                "Account Number": "accountNumber",
                "Item Type Code": "itemTypeCode",
                "Account Type": "accountType",
                "Reference Number": "referenceNumber",
                "Issued Date": "issuedDate",
                "Check Number (Required)": "checkNumber"
            };

            Object.keys(checkTemplateRecord).map(key => {
                const val = checkTemplateRecord[key];

                if(key.includes("mapColumn")) {
                    checkTemplateRecord[key] = "";
                }
            });

            const nextID = PositivePayCheckTemplate.nextID();
            const customItemTypeCodes = checkTemplateRecord.customItemTypeCodes;
            const customCodeMap = {
                "issue": "I",
                "stop": "S",
                "void": "V"
            }
            const createCustomItemTypeCodes = customItemTypeCodes.reduce((acc, item) => {
                const record = item.record;
                
                // Uncomment if we need to support dynamic values. For now, we are using happy path values.
                //record.itemTypeMappingValue = record.itemTypeMappingValue ? record.itemTypeMappingValue : customCodeMap[record.itemTypeMapping];
                record.itemTypeMappingValue = customCodeMap[record.itemTypeMapping];

                acc.push(
                    CustomItemTypeCode.create({...record, template: nextID })
                );
                return acc;
            }, []);

            const mergedCheckTemplate = BCMSDefaultModel.mergeRecords(checkTemplateRecord, defaultCheckTemplate.record);
            
            let newCheckTemplate = PositivePayCheckTemplate.init({ ...mergedCheckTemplate, id: nextID });
            newCheckTemplate.customItemTypeCodes = customItemTypeCodes.reduce((acc, item) => {
                acc.push(
                    item.id
                );
                return acc;
            }, []);

            
            const updates = [ PositivePayCheckTemplate.create(newCheckTemplate), ...createCustomItemTypeCodes ];
            BCMSRecord.batch(updates, context, Classes);
            navigateCallback({ state: { newTemplate: newCheckTemplate } });
            window.scrollTo(0, 0);
        },

        onMobileCloseButtonClick: id => evt => {
            let customItemTypeCodes = state.ui.checkTemplate.customItemTypeCodes;
            customItemTypeCodes = customItemTypeCodes.reduce((acc, item) => {
                if(item.id === id) {
                    acc.push(
                        CustomItemTypeCodeUI.init({ ...item.record, mobile: { isActive: !item.mobile.isActive } })
                    );
                } else {
                    acc.push(item);
                }
                return acc;
            }, []);

            BCMSStateManagerCRUD.init(state)
            .update(["ui", "checkTemplate", "customItemTypeCodes"], customItemTypeCodes)
            .submit(setState);
        }
    });
    return (
        <PageContainer className="tas-positive-pay-create-template-page" state={{ data, ui }} controller={controller}>
            <ImportFileNewTemplatePageContent />
        </PageContainer>
    )
}

export const getDefaultData = (props) => {
    //const checkTemplate = TASPositivePayCreateCheckTemplateForm.init();
    
    var selectedColumns = [
        { id: "0", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "1", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "2", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "3", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "4", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "5", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "6", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "7", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
    ];

    var mapColumns = [
        { id: "0", value: "(Exclude Column)", class: "placeholder-item" },
        { id: "1", value: "Payee", class: "payee" },
        { id: "2", value: "Amount", class: "amount", isSelected: false },
        { id: "3", value: "Account Number", class: "account-number", isSelected: false },
        { id: "4", value: "Item Type Code", class: "item-type-code", isSelected: false },
        { id: "5", value: "Account Type", class: "account-type", isSelected: false },
        { id: "6", value: "Reference Number", class: "reference-number", isSelected: false },
        { id: "7", value: "Check Number (Required)", class: "check-number", isSelected: false },
        { id: "8", value: "Issued Date", class: "issued-date", isSelected: false },
    ];

    // start: Not currently in use, but may be when we support editing.
    /*const getMapColumn = value => {
        return mapColumns.filter(item => item.value === value)[0];
    };*/

    // This object will update "selectedColumns" and "mapColumns"
    // so that we can load in stored values from the model or from a preexisting template.
    /*Object.keys(checkTemplate).map(key => {
        if(key.includes("mapColumn")) {
            var mapColumnValue = checkTemplate[key];

            // Map "mapColumn1" to id "0" since that is the indexing used in the "selectedColumns" array.
            var selectedColumnIDInt = parseInt(key.replace("_mapColumn", ""));
            var selectedColumnID = String(selectedColumnIDInt - 1);

            selectedColumns.reduce((acc, column) => {
                if(column.id === selectedColumnID) {
                    if(mapColumnValue !== "(Exclude Column)") {
                        column.value = getMapColumn(mapColumnValue);
                        // The "mapColumns" object above will be updated by reference.
                        column.value.isSelected = true;
                    }
                }
                acc.push(column);
                return acc;
            }, []);
        }
        return key;
    });*/
    // end: Not currently in use, but may be when we support editing.

    const hasProps = props ?? false;
    const hasTemplateID = hasProps ? props.id : false;

    let customItemTypeCodes = undefined;
    let customItemTypeCodeIDs = hasTemplateID
    ? PositivePayCheckTemplate.initByID(props.id).customItemTypeCodes
    : [];

    if(customItemTypeCodeIDs.length === 0) {
        const nextID = parseInt(CustomItemTypeCode.nextID());
        let count = nextID;
        customItemTypeCodes = [
            CustomItemTypeCode.init({ id: `${nextID}`, itemTypeMapping: "issue", itemTypeMappingValue: "" }),
            CustomItemTypeCode.init({ id: `${nextID + 1}`, itemTypeMapping: "stop", itemTypeMappingValue: "" }),
            CustomItemTypeCode.init({ id: `${nextID + 2}`, itemTypeMapping: "void", itemTypeMappingValue: "" }),
        ];
    } else {
        customItemTypeCodes = customItemTypeCodeIDs.reduce((acc, item) => {
            acc.push(
                CustomItemTypeCode.initByID(item)
            );
            return acc;
        }, []);
    }

    return {
        checkTemplate: TASPositivePayCreateCheckTemplateForm.init({ customItemTypeCodes: customItemTypeCodes }),
        checkTemplateRows: PositivePayCheckFileRow.initAll().filter(item => {
            return item;
            //return item.positivePayCheckFile.id === "0";
        }),
        selectedColumns: selectedColumns,
        mapColumns: mapColumns,
    };
};

export const getDefaultUI = (_form) => {
    let customItemTypeCodes = _form.customItemTypeCodes;
    customItemTypeCodes = customItemTypeCodes.reduce((acc, item, index) => {
        acc.push(
            CustomItemTypeCodeUI.init({ id: item.id, customItemTypeCode: item, mobile: { isActive: index === 0 } })
        );
        return acc;
    }, []);

    const checkTemplate = TASPositivePayCreateCheckTemplateFormUI.init({ form: _form, customItemTypeCodes: customItemTypeCodes });

    return {
        checkTemplate: checkTemplate,
    };
};


/**
 * @function ImportFileNewTemplatePageContent
 * @return {React.Component}
 * @exported true
 * @description The page content component for the ImportFileNewTemplatePage.
 */
function ImportFileNewTemplatePageContent(props) {
    const { ui, data, onChangeCheckTemp, onAdd } = useStateManager();
    const chosenFile = data.checkTemplate.positivePayCheckFile ? data.checkTemplate.positivePayCheckFile.description : `No file chosen`;
    const checkTemplate = data.checkTemplate;
    const didSelectItemTypeCode = data.selectedColumns.filter(item => item.value.value === "Item Type Code").length > 0;
    const navigate = useRoute();
    const allMapColumnsAreSelected = data.checkTemplate.allMapColumnsAreSelected;
    const primaryButtonText = allMapColumnsAreSelected ? "Save" : "Next";

    const navigateCallback = state => {
        navigate("/tasPositivePayImportChecks", state);
    };

    const onCancel = evt => {
        navigate("/tasPositivePayImportChecks");
        window.scrollTo(0, 0);
    }

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="Create Template" />
                <FormContainer className="small-form-width first-half-of-page-forms">
                    <FormRowView text="File">
                        <div className="flex-container flex-horizontal align-center choose-file-container">
                            <div className="set-width secondary-button-container">
                                <SecondaryButton text="Choose File"
                                dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Choose_File_Button` }
                                onClick={ onChangeCheckTemp("positivePayCheckFile") } />
                            </div>
                            <p className="set-width">{ chosenFile }</p>
                        </div>
                    </FormRowView>
                    <FormRowView>
                        <p>Maximum file size: 500 KB</p>
                        <p>Valid file extensions: .csv, .txt</p>
                    </FormRowView>
                    <FormRowView className="import-attached-file-row" text="Import Attached File">
                        <UIRadioGroup value={ checkTemplate.importAttachedFile }>
                            <DisabledPrompt>
                                <UIRadio className="ui-group-item set-width"
                                label="Yes" value="true" />
                            </DisabledPrompt>
                            <UIRadio className="ui-group-item set-width"
                            dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Import_Attached_File_No_Radio` }
                            label="No" value="false" />
                        </UIRadioGroup>
                    </FormRowView>
                    <FormRowView className="template-name-input-row" text="Template Name">
                        <UITextInput value={ checkTemplate.name }
                        dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Template_Name_Input` }
                        onChange={ onChangeCheckTemp("name") } />
                    </FormRowView>
                    <FormRowView className="input-decimals-into-amounts-row" text={
                        <div className="flex-container flex-horizontal align-center">
                            <p className="label-with-icon">Input Decimals into Amount</p>
                            <SVGAsset id="InfoIcon" />
                        </div>
                    }>
                        <DisabledPrompt>
                            <CustomSelect value={ checkTemplate.inputDecimalsIntoAmount }
                            dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Input_Decimals_Into_Amounts_Select` }
                            onChange={ onChangeCheckTemp("inputDecimalsIntoAmount") }>
                                <CustomSelectItem value="yes" dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Input_Decimals_Into_Amounts_Option_Yes` }>Yes</CustomSelectItem>
                                <CustomSelectItem value="no" dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Input_Decimals_Into_Amounts_Option_No` }>No</CustomSelectItem>
                            </CustomSelect>
                        </DisabledPrompt>
                    </FormRowView>
                    <TASPositivePayCreateTemplateFileExcludeWidget/>
                </FormContainer>
                <TASPositivePayCreateTemplateFileFormatWidget/>
                { ui.checkTemplate.showFilePreview.isActive && <TASPositivePayCreateTemplateFilePreviewWidget/> }
                <TASPositivePayCreateTemplateFileMapColumnsWidget/>
                { didSelectItemTypeCode && <TASPositivePayCreateTemplateItemTypeMappingWidget/> }
                <TASPositivePayCreateTemplateFileMappingPreviewWidget/>

                <div className="button-container flex-container flex-horizontal">
                    <SecondaryButton dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Cancel_Button` } text="Cancel" onClick={ onCancel } />
                    <PrimaryButton dataPoint={ `TAS_Positive_Pay_Create_Template_Page_Add_Button` } text={ primaryButtonText } onClick={ onAdd(navigateCallback) } />
                </div>
            </CenterContentContainer>
        </PageContentView>
    )
}